/* Luxuntique 2026 — live Google reviews
   The Google Review Plus app block (rendered hidden inside the section) puts the
   shop's live Google data on the page as window.grp_app_metadata. This element
   redraws it in the Luxuntique design:
   - mode "list": the newest reviews of 3 stars and up (home page)
   - mode "product": a review that names this product's model, if there is one
   It also refreshes every [data-lux-google-rating] line on the page.
   If the data is missing, the server-rendered fallback stays as it is. */

const MIN_STARS = 3;

/** A Google address we trust to link to, or '' @param {unknown} value */
function googleUrl(value) {
  try {
    const url = new URL(String(value || ''));
    const host = url.hostname.replace(/^www\./, '');
    const google = host === 'google.com' || host.endsWith('.google.com') || ['share.google', 'g.page', 'goo.gl'].includes(host);
    return url.protocol === 'https:' && google ? url.href : '';
  } catch {
    return '';
  }
}

/**
 * Where a review can be read on Google. Google's data has no link to a single review, so: the
 * reviewer's Google profile (lists their reviews, this one included), else the shop's Google listing.
 * @param {any} review @param {any} data
 */
function reviewLink(review, data) {
  return googleUrl(review.author_url) || googleUrl(data && data.url);
}

/** @returns {{rating: string, total: number, reviews: Array<object>} | null} */
function googleData() {
  const data = window.grp_app_metadata;
  if (!data || !Array.isArray(data.reviews)) return null;
  return data;
}

/** Lower case, letters and digits only, single spaces, padded for whole-word checks. */
function normalise(text) {
  return ` ${String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `;
}

function goodReviews(data) {
  return data.reviews
    .filter((review) => Number(review.rating) >= MIN_STARS && String(review.text || '').trim() !== '')
    .sort((a, b) => Number(b.time || 0) - Number(a.time || 0));
}

/**
 * Best reviews first: higher rating, then with a photo, then more detailed, then newest.
 * @param {Array<object>} reviews
 */
function bestFirst(reviews) {
  const score = (review) =>
    Number(review.rating || 0) * 1000 +
    (Array.isArray(review.image_urls) && review.image_urls.length ? 200 : 0) +
    Math.min(String(review.text || '').trim().length, 600) / 4;
  return [...reviews].sort((a, b) => score(b) - score(a) || Number(b.time || 0) - Number(a.time || 0));
}

function stars(count) {
  return '★'.repeat(Math.max(0, Math.min(5, Math.round(Number(count) || 0))));
}

class LuxGoogleReviews extends HTMLElement {
  connectedCallback() {
    // The app's inline data script runs before deferred modules, but wait for the DOM to be safe.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.render(), { once: true });
    } else {
      this.render();
    }
  }

  render() {
    const data = googleData();
    if (!data) return;
    this.data = data;

    this.updateRatingLines(data);

    if (this.dataset.mode === 'product') {
      this.renderProductReview(data);
    } else {
      this.renderList(data);
    }
  }

  updateRatingLines(data) {
    const rating = String(data.rating || '').trim();
    const total = Number(data.total_num_of_reviews) || 0;
    if (!rating || !total) return;

    for (const line of document.querySelectorAll('[data-lux-google-rating]')) {
      const template = line.getAttribute('data-lux-google-rating') || '';
      if (!template) continue;
      line.textContent = template.replace('{rating}', rating).replace('{count}', String(total));
    }
  }

  renderList(data) {
    const list = this.querySelector('[data-lux-reviews-list]');
    const template = this.querySelector('template[data-lux-review-template]');
    if (!list || !template) return;

    const marquee = this.dataset.layout === 'marquee';
    const good = goodReviews(data);
    const reviews = marquee ? bestFirst(good) : good.slice(0, Number(this.dataset.limit) || 3);
    if (reviews.length === 0) return;

    const items = reviews.map((review) => this.buildItem(template, review));
    list.replaceChildren(...items);
    this.hidden = false;
    // Show "Read more on Google" only on reviews that are cut off (needs layout, so after insert)
    this.#markLongReviews(list);

    if (marquee) this.#startPan(list);

    this.hidden = false;
    this.setAttribute('data-live', 'true');
  }

  /** @param {Element} scope */
  #markLongReviews(scope) {
    for (const quote of scope.querySelectorAll('[data-field="quote"]')) {
      const more = quote.closest('figure')?.querySelector('[data-field="more"]');
      if (more instanceof HTMLElement) more.hidden = quote.scrollHeight <= quote.clientHeight + 2;
    }
  }

  /**
   * Moving strip: the reviews are repeated once so the loop is seamless.
   * The copy is hidden from screen readers and keyboard focus.
   * @param {HTMLElement} list
   */
  #startPan(list) {
    const originals = [...list.children];
    for (const item of originals) {
      const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-clone', '');
      clone.inert = true;
      list.append(clone);
    }
    // About 7 seconds per review keeps the reading pace calm
    list.style.setProperty('--lux-pan-duration', `${Math.max(originals.length * 7, 28)}s`);
    list.setAttribute('data-marquee', '');

    const toggle = this.querySelector('[data-lux-pan-toggle]');
    if (toggle instanceof HTMLButtonElement && !toggle.dataset.bound) {
      toggle.dataset.bound = 'true';
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        const paused = this.toggleAttribute('data-paused');
        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        toggle.setAttribute('aria-label', (paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause) || '');
      });
    }
  }

  renderProductReview(data) {
    const template = this.querySelector('template[data-lux-review-template]');
    const feature = this.querySelector('[data-lux-feature]');
    const latest = this.querySelector('[data-lux-latest]');
    if (!template) return;

    const reviews = goodReviews(data);
    const model = normalise(this.dataset.model);
    const match = model.trim().length >= 3 ? reviews.find((review) => normalise(review.text).includes(model)) : null;

    if (match && feature) {
      const slot = feature.querySelector('[data-lux-review-slot]');
      if (!slot) return;
      slot.replaceChildren(this.buildItem(template, match));
      feature.hidden = false;
      if (latest) latest.hidden = true;
      this.hidden = false;
      this.#markLongReviews(slot);
    } else if (this.dataset.fallback === 'true' && latest && reviews.length > 0) {
      const list = latest.querySelector('[data-lux-reviews-list]');
      if (!list) return;
      const limit = Number(this.dataset.limit) || 3;
      list.replaceChildren(...reviews.slice(0, limit).map((review) => this.buildItem(template, review)));
      latest.hidden = false;
      if (feature) feature.hidden = true;
      this.hidden = false;
      this.#markLongReviews(list);
    } else {
      return;
    }

    this.hidden = false;
    this.setAttribute('data-live', 'true');
  }

  /**
   * @param {HTMLTemplateElement} template
   * @param {object} review
   */
  buildItem(template, review) {
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const set = (name, value) => {
      const el = fragment.querySelector(`[data-field="${name}"]`);
      if (el) el.textContent = value;
    };

    set('quote', `“${String(review.text).trim()}”`);
    set('name', review.author_name || '');
    set('stars', stars(review.rating));
    set('stars-label', `${Math.round(Number(review.rating) || 0)} / 5`);

    // "Google ✓" and "Read more" open this review's place on Google; fall back to the section's link
    const link = reviewLink(review, this.data);
    for (const anchor of fragment.querySelectorAll('[data-field="source-link"], [data-field="more"]')) {
      if (!(anchor instanceof HTMLAnchorElement)) continue;
      if (link) anchor.href = link;
      else if (!anchor.getAttribute('href')) anchor.removeAttribute('target');
    }

    const photo = fragment.querySelector('[data-field="photo"]');
    const image = Array.isArray(review.image_urls) ? review.image_urls[0] : null;
    if (photo instanceof HTMLImageElement && image) {
      photo.src = image;
      photo.hidden = false;
    } else if (photo) {
      photo.remove();
    }

    return fragment;
  }
}

if (!customElements.get('lux-google-reviews')) {
  customElements.define('lux-google-reviews', LuxGoogleReviews);
}
